import type { AxiosRequestConfig } from 'axios'
import apiClient from './apiClient'
import type { ApiError } from './apiClient'
import { platformHeaders } from './platformHeaders'

// Shoppable list APIs cap page_size at 100; asking for more is never useful.
export const SHOPPABLE_MAX_PAGE_SIZE = 100
export const clampPageSize = (pageSize: number) =>
  Math.min(Math.max(Math.floor(pageSize) || 1, 1), SHOPPABLE_MAX_PAGE_SIZE)

// Headers every Shoppable API call carries; brand-scoped calls also send the brand.
export const shoppableHeaders = (brandId?: string) => platformHeaders('SHOPPABLE', brandId)

// Rethrows an API failure as an ApiError, using the fallback when the backend gave no message.
export const toApiError = (error: unknown, fallbackMessage: string): unknown => {
  if (error && typeof error === 'object' && 'message' in error) {
    const apiError = error as ApiError
    return {
      message: apiError.message || fallbackMessage,
      status: apiError.status,
      code: apiError.code,
      details: apiError.details,
    } as ApiError
  }
  return error
}

export async function shoppableGet<T>(
  url: string,
  brandId: string | undefined,
  fallbackMessage: string,
  config?: AxiosRequestConfig
): Promise<T> {
  try {
    return await apiClient.get<T>(url, { ...config, headers: shoppableHeaders(brandId) })
  } catch (error) {
    throw toApiError(error, fallbackMessage)
  }
}

export async function shoppablePatch<T, D = unknown>(
  url: string,
  data: D,
  brandId: string | undefined,
  fallbackMessage: string,
  config?: AxiosRequestConfig
): Promise<T> {
  try {
    return await apiClient.patch<T, D>(url, data, { ...config, headers: shoppableHeaders(brandId) })
  } catch (error) {
    throw toApiError(error, fallbackMessage)
  }
}

export async function shoppablePost<T, D = unknown>(
  url: string,
  data: D,
  brandId: string | undefined,
  fallbackMessage: string,
  config?: AxiosRequestConfig
): Promise<T> {
  try {
    return await apiClient.post<T, D>(url, data, { ...config, headers: shoppableHeaders(brandId) })
  } catch (error) {
    throw toApiError(error, fallbackMessage)
  }
}
